import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import Download from '@mui/icons-material/Download'
import Downloading from '@mui/icons-material/Downloading'
import DataTable from '../../../../../components/Tables/DataTable'
import LoadingBackdrop from '../../../../../components/Loading/LoadingBackdrop'
import ContentDialog from '../../../../../components/Dialogs/ContentDialog'
import ConfirmDialog from '../../../../../components/Dialogs/ConfirmDialog'
import { useToast } from '../../../../../core/context/ToastContext'
import { useCommunityMembers } from '../../../../../core/hooks/useCommunityMembers'
import { useDownloadFile } from '../../../../../core/hooks/useDownloadFile'
import CommunityIntegrationController from '../../../../../core/controllers/CommunityIntegrationController'
import { ExportFileTypeEnum } from '../../../../../core/controllers/ReportDownloadController'
import { TCommunityMember } from '../../../../../core/types/CommunityMember'
import { getCommunityMemberColumns } from './CommunityMemberConsts'

type TStatusFilter = 'all' | 'joined' | 'pending'

const hasJoinedFromFilter = (filter: TStatusFilter): boolean | null => {
  if (filter === 'joined') return true
  if (filter === 'pending') return false
  return null
}

function ProductCommunityMembers() {
  const { id } = useParams<{ id: string }>()
  const { showToast } = useToast()

  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [name, setName] = useState('')
  const [statusFilter, setStatusFilter] = useState<TStatusFilter>('all')
  const [renewingId, setRenewingId] = useState<string | null>(null)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [inviteLink, setInviteLink] = useState<string | null>(null)
  const [openInviteDialog, setOpenInviteDialog] = useState(false)
  const [memberToRemove, setMemberToRemove] = useState<TCommunityMember | null>(
    null
  )
  const [exportMenuAnchor, setExportMenuAnchor] = useState<HTMLElement | null>(
    null
  )

  const { loading, data, error, execute, clear } = useCommunityMembers({
    productId: id || '',
    page,
    rows: rowsPerPage,
    name,
    hasJoined: hasJoinedFromFilter(statusFilter),
  })

  const { downloading, download } = useDownloadFile({
    apiDefinition: (fileType) =>
      CommunityIntegrationController.getMembersFile({
        productId: id || '',
        name,
        hasJoined: hasJoinedFromFilter(statusFilter),
        exportFileType: fileType,
      }),
    getFileName: (fileType) =>
      `membros_comunidade_${new Date().toISOString().replace(/[:.]/g, '_')}.${
        fileType === ExportFileTypeEnum.XLS ? 'xlsx' : 'csv'
      }`,
    onError: (message) => showToast(message, 'error'),
  })

  React.useEffect(() => {
    if (error !== '') {
      showToast(error, 'error')
      clear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error])

  const handleChangePage = (newPage: number) => setPage(newPage)

  const handleChangeRowsPerPage = (rows: number) => {
    setRowsPerPage(rows)
    setPage(0)
  }

  const handleRenewClick = async (member: TCommunityMember) => {
    setRenewingId(member.id)
    try {
      const response = await CommunityIntegrationController.renewTelegramInviteLink(
        member.orderId
      )
      if (!response.success) {
        showToast(response.error, 'error')
        return
      }
      setInviteLink(response.data as string)
      setOpenInviteDialog(true)
      execute()
    } finally {
      setRenewingId(null)
    }
  }

  const handleResendEmailClick = async (member: TCommunityMember) => {
    setResendingId(member.id)
    try {
      const response = await CommunityIntegrationController.resendEmail({
        memberId: member.id,
      })
      if (!response.success) {
        showToast(response.error, 'error')
        return
      }
      showToast('E-mail reenviado ao cliente', 'success')
      execute()
    } finally {
      setResendingId(null)
    }
  }

  const handleRemoveClick = (member: TCommunityMember) =>
    setMemberToRemove(member)

  const handleConfirmRemove = async () => {
    if (!memberToRemove) return
    const member = memberToRemove
    setRemovingId(member.id)
    try {
      const response = await CommunityIntegrationController.removeMember({
        memberId: member.id,
      })
      if (!response.success) {
        showToast(response.error, 'error')
        return
      }
      showToast('Membro removido do grupo', 'success')
      execute()
    } finally {
      setRemovingId(null)
    }
  }

  const handleCopyInviteLink = async () => {
    if (inviteLink) {
      try {
        await navigator.clipboard.writeText(inviteLink)
        showToast('Link copiado', 'success')
      } catch (err) {
        showToast(
          'Não foi possível copiar o link para a área de transferência.',
          'error'
        )
      }
    }
    setOpenInviteDialog(false)
    return true
  }

  const handleExportClick = (fileType: ExportFileTypeEnum) => {
    setExportMenuAnchor(null)
    download(fileType)
  }

  const columns = getCommunityMemberColumns({
    onRenewClick: handleRenewClick,
    renewingId,
    onResendEmailClick: handleResendEmailClick,
    resendingId,
    onRemoveClick: handleRemoveClick,
    removingId,
  })

  return (
    <Box>
      <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
        <TextField
          size="small"
          placeholder="Pesquisar por nome"
          value={name}
          onChange={(e) => {
            setName(e.target.value)
            setPage(0)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <img alt="Pesquisar" src="/assets/icons/search/search.svg" />
              </InputAdornment>
            ),
          }}
          sx={{ backgroundColor: 'white', minWidth: 260 }}
        />

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="community-status-filter-label">Status</InputLabel>
          <Select
            labelId="community-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value as TStatusFilter)
              setPage(0)
            }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="joined">Entrou no grupo</MenuItem>
            <MenuItem value="pending">Aguardando entrada</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={downloading ? <Downloading /> : <Download />}
          disabled={downloading}
          onClick={(e) => setExportMenuAnchor(e.currentTarget)}
          sx={{ textTransform: 'none', ml: 'auto' }}
        >
          {downloading ? 'Exportando...' : 'Exportar'}
        </Button>
        <Menu
          anchorEl={exportMenuAnchor}
          open={Boolean(exportMenuAnchor)}
          onClose={() => setExportMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleExportClick(ExportFileTypeEnum.XLS)}>
            Excel (.xlsx)
          </MenuItem>
          <MenuItem onClick={() => handleExportClick(ExportFileTypeEnum.CSV)}>
            CSV (.csv)
          </MenuItem>
        </Menu>
      </Stack>

      <DataTable
        columns={columns}
        data={data}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <ContentDialog
        open={openInviteDialog}
        onClose={() => setOpenInviteDialog(false)}
        title="Link de convite gerado"
        labelPrositive="Copiar link"
        onPositive={handleCopyInviteLink}
      >
        <Box>
          <Typography sx={{ mb: 2 }}>
            Um novo link de convite do grupo no Telegram foi gerado. Use o
            link abaixo:
          </Typography>

          <Alert
            variant="outlined"
            severity="info"
            sx={{ wordBreak: 'break-all', fontSize: '0.9rem' }}
          >
            {inviteLink}
          </Alert>

          <Typography sx={{ mt: 2 }} variant="body2" color="text.secondary">
            Clique em &quot;Copiar link&quot; para enviar esse link ao
            cliente.
          </Typography>
        </Box>
      </ContentDialog>

      <ConfirmDialog
        open={memberToRemove !== null}
        onClose={() => setMemberToRemove(null)}
        title="Remover membro do grupo?"
        message={`Tem certeza que deseja remover ${memberToRemove?.customerName ?? ''} do grupo? Isso não cancela a cobrança nem o pedido, só a presença no grupo.`}
        onYes={handleConfirmRemove}
      />

      <LoadingBackdrop open={loading} />
    </Box>
  )
}

export default ProductCommunityMembers
